import { HttpClient } from '@/shared';
import { IAuthRepository } from '../../application/ports/outbound/IAuthRepository';
import { AppUser } from '@/features/app-user/application/domain/entities/account';
import Account from '@/features/account/application/domain/entities/account';
import { AuthResponse } from '@/shared/auth';
import AppUserMapper from '@/features/app-user/infra/mapper/appUser.mapper';
import AccountMapper from '@/features/account/infra/mapper/account.mapper';
import { AccountListItem } from '../../application/domain/entities/accountListItem';

export interface AuthData {
    user : AppUser,
    account : Account | null,
    role : string,
    permissions : string[],
}

export class ApiAuthRepository implements IAuthRepository {
  constructor(private readonly httpClient: HttpClient) {}
  async logout(): Promise<void> {
    await this.httpClient.post('/auth/logout');
    this.httpClient.setAccessToken(null);
  }

  /**
   * Exchange OAuth2 grant code for access and refresh tokens
   *
   * @param grantCode - OAuth2 grant code from callback
   * @returns Promise resolving to AuthResponse
   */

  async exchangeGrantToken(grantCode: string): Promise<AuthData> {
    // MOCK: Using mock service
    // return mockAuthService.mockGrantTokenExchange(grantCode);

    // TODO: [BACKEND INTEGRATION] Uncomment when backend is ready
    const response = await this.httpClient.post<AuthResponse>('/auth', {
      grantCode,
    });
    this.httpClient.setAccessToken(response.data.accessToken);
    const authData: AuthData = {
      user: AppUserMapper.mapAppUserDtoToAppUser(response.data.appUser),
      account: AccountMapper.mapAccountDtoToAccount(response.data.appUser.account ?? null),
      role: response.data.roleKey,
      permissions: response.data.permissions,
    };
    return authData;
  }

  /**
   * Who the browser's session cookie says we are, without signing in again.
   *
   * The client's picture of the user is per-TAB while the cookie authorising its requests is shared
   * by the whole browser, so the two drift. This re-derives that picture from the credential the
   * browser is actually sending — the only thing that stays correct across tabs and reloads.
   *
   * Rejects with a 401 when there is no valid cookie. That is the honest answer to "am I signed
   * in?", not a failure, and the caller treats it as such.
   */
  async getSession(): Promise<AuthData> {
    const response = await this.httpClient.get<AuthResponse>('/auth/session');
    this.httpClient.setAccessToken(response.data.accessToken);
    return {
      user: AppUserMapper.mapAppUserDtoToAppUser(response.data.appUser),
      account: AccountMapper.mapAccountDtoToAccount(response.data.appUser.account ?? null),
      role: response.data.roleKey,
      permissions: response.data.permissions,
    };
  }

  /**
   * Refresh access token using refresh token
   *
   * @param refreshToken - Refresh token
   * @returns Promise resolving to new TokenResponse
   *
   * TODO: [BACKEND INTEGRATION]
   * Replace mock implementation with:
   * ```
   * const response = await httpClient.post<TokenResponse>('/auth/refresh', {
   *   refreshToken
   * });
   * return response.data;
   * ```
   *
   * TODO: [TRANSITION TO HTTPONLY COOKIES]
   * When using httpOnly cookies, refresh token will be sent automatically
   * ```
   * const response = await httpClient.post<TokenResponse>('/auth/refresh');
   * return response.data;
   * ```
   */

  async getAccountList(): Promise<AccountListItem[]> {
    const response =
      await this.httpClient.get<Array<{ id: string; accountName: string }>>(
        '/account/all-accounts'
      );

    return response.data.map((account) => ({
      id: account.id,
      name: account.accountName,
    }));
  }

  async selectAccount(accountId: string): Promise<Account | null> {
    // First, call the select-account endpoint
    const selectResponse = await this.httpClient.post('/account/select-account', {
      accountId: accountId,
    });

    // Check if account selection was successful
    if (selectResponse.statusCode !== 201) {
      throw new Error('Failed to select account');
    }

    // Then fetch the account details
    const response = await this.httpClient.get<Account>(`/account/${accountId}`);
    return response.data;
  }
}
